import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { Spinner } from '../ui/spinner';
import { livechatMiningAPI } from '../../services/api';

const BUCKET_OPTIONS = [
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

// bucket_start comes from Postgres date_trunc as an absolute UTC timestamp — formatting in
// the browser's local timezone would shift weekly labels by a day (or monthly labels into
// the preceding month) for anyone west of UTC. The year is always included (not just for
// "month") since this chart intentionally spans the pattern's full history — without it,
// weekly labels like "02 Jan" from different years would be indistinguishable.
const formatBucketLabel = (iso, bucket) => {
  const date = new Date(iso);
  return bucket === 'month'
    ? date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' })
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC' });
};

const TrendTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded border border-white/10 bg-surface-1 px-2 py-1.5 text-[11px]">
      <p className="font-bold text-ink">{row.label}</p>
      <p className="text-ink-dim">{row.thread_count} conversations</p>
    </div>
  );
};

// Pattern volume over its full history — deliberately NOT limited to the "active chats"
// date range, since a narrow default range would collapse the chart to a single bucket —
// narrowed by the same lifecycle/activity/actionability/signal type/topic/search filters as
// the pattern card's thread table, so it matches what's actually shown for this pattern.
const PatternTrendChart = ({ pattern, params }) => {
  const [bucket, setBucket] = useState('week');
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pattern) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const resp = await livechatMiningAPI.getPatternTrend(
          pattern, bucket, params?.lifecycleState, params?.activityState,
          params?.actionability, params?.signalType, params?.topic, params?.search,
        );
        if (cancelled) return;
        setPoints((resp.data.points || []).map((p) => ({
          label: formatBucketLabel(p.bucket_start, bucket),
          thread_count: p.thread_count,
        })));
      } catch {
        if (!cancelled) setPoints([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [
    pattern, bucket, params?.lifecycleState, params?.activityState, params?.actionability,
    params?.signalType, params?.topic, params?.search,
  ]);

  return (
    <div className="mb-4 pb-3 border-b border-white/10">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">Volume over time</span>
        <ToggleGroup value={bucket} onChange={setBucket}>
          {BUCKET_OPTIONS.map((o) => <ToggleItem key={o.value} value={o.value}>{o.label}</ToggleItem>)}
        </ToggleGroup>
      </div>
      {loading || points === null ? (
        <div className="flex justify-center py-4"><Spinner size="sm" /></div>
      ) : points.length === 0 ? (
        <div className="text-sm text-ink-dim py-2">No trend data.</div>
      ) : (
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#262c3d" vertical={false} />
            <XAxis
              dataKey="label" tick={{ fontSize: 9, fill: '#9aa0ad' }} tickLine={false}
              axisLine={{ stroke: '#262c3d' }}
            />
            <YAxis
              allowDecimals={false} tick={{ fontSize: 9, fill: '#9aa0ad' }} tickLine={false}
              axisLine={{ stroke: '#262c3d' }} width={28}
            />
            <Tooltip content={<TrendTooltip />} />
            <Line
              type="monotone" dataKey="thread_count" stroke="#00BD71" strokeWidth={2}
              dot={points.length <= 40} activeDot={{ r: 4 }} isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default PatternTrendChart;
